import ReactECharts from 'echarts-for-react';

/**
 * RaisyStackChart_2 — "minimal editorial" variant of RaisyStackChart (blog01).
 *
 * Same data, same chart family (sunburst — hierarchical part-to-whole).
 * Opaque white card, generous whitespace, thin strokes, muted slate palette
 * with a single teal accent, small typography, no gradients/glows/shadows.
 * Quiet magazine-infographic feel.
 */

const PALETTE = {
  teal: '#0AB3BF', // single accent (Entity level)
  slate: '#64748B', // Process level
  slateLight: '#94A3B8', // Bot level
  slatePale: '#CBD5E1', // Kernel level (muted neutral)
  ink: '#1E293B', // text on light fills
  onTeal: '#083344', // text on the teal accent
  white: '#FFFFFF',
  surface: '#FFFFFF',
  border: '#E2E8F0',
};

interface StackNode {
  name: string;
  value?: number;
  itemStyle?: { color: string };
  children?: StackNode[];
}

const stackData: StackNode[] = [
  {
    name: 'Entity',
    itemStyle: { color: PALETTE.teal },
    children: [
      {
        name: 'Process',
        itemStyle: { color: PALETTE.slate },
        children: [
          {
            name: 'Bot',
            itemStyle: { color: PALETTE.slateLight },
            children: [
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.slatePale } },
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.slatePale } },
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.slatePale } },
            ],
          },
          {
            name: 'Bot',
            itemStyle: { color: PALETTE.slateLight },
            children: [
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.slatePale } },
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.slatePale } },
            ],
          },
        ],
      },
      {
        name: 'Process',
        itemStyle: { color: PALETTE.slate },
        children: [
          {
            name: 'Bot',
            itemStyle: { color: PALETTE.slateLight },
            children: [
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.slatePale } },
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.slatePale } },
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.slatePale } },
              { name: 'Kernel', value: 1, itemStyle: { color: PALETTE.slatePale } },
            ],
          },
        ],
      },
    ],
  },
];

/** ONE shared level-name + description source used by label and tooltip. */
const levelMeta: Record<string, string> = {
  Entity: 'Who acts — companies, products, people',
  Process: 'What they do repeatedly — workflows in a domain',
  Bot: 'Who executes each step — bots or humans',
  Kernel: 'What the actor knows — rules, context, schema',
};

const labelOf = (name: string) => name;

const tooltipOf = (name: string) =>
  `${name}<br/><span style="color:#94A3B8">${levelMeta[name] ?? 'Raisy building block'}</span>`;

export const RaisyStackChart_2 = ({ height = 380 }: { height?: number }) => {
  const option = {
    backgroundColor: PALETTE.surface,
    tooltip: {
      trigger: 'item',
      backgroundColor: PALETTE.ink,
      borderColor: PALETTE.slate,
      textStyle: { color: '#F8FAFC', fontSize: 12 },
      formatter: (p: { name: string }) => tooltipOf(p.name),
    },
    series: [
      {
        type: 'sunburst',
        data: stackData,
        radius: [0, '88%'],
        center: ['50%', '52%'],
        sort: undefined,
        emphasis: { focus: 'ancestor' },
        label: {
          rotate: 0,
          color: PALETTE.ink,
          fontSize: 12,
          fontWeight: 600,
          formatter: (p: { name: string }) => labelOf(p.name),
        },
        itemStyle: { borderColor: PALETTE.surface, borderWidth: 1 },
        levels: [
          {},
          { r0: '0%', r: '34%', label: { fontSize: 13, color: PALETTE.onTeal, fontWeight: 700 } },
          { r0: '34%', r: '62%', label: { fontSize: 12, color: PALETTE.white, fontWeight: 600 } },
          { r0: '62%', r: '82%', label: { fontSize: 12, color: PALETTE.ink, fontWeight: 600 } },
          { r0: '82%', r: '100%', label: { fontSize: 11, color: PALETTE.slate, fontWeight: 500 } },
        ],
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 bg-white p-6">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-4 text-xs text-slate-500 text-center">
        Figure 2. The Raisy stack, top-down: an Entity owns Processes, each Process is executed by Bots, and each Bot
        loads Kernels — the rules it knows. Click a segment to zoom; hover for the level definition.
      </p>
    </div>
  );
};
